<template>
    <div class="mt-6 rounded-lg border border-gray-200 bg-white">
        <div class="flex items-center justify-between border-b px-5 py-4">
            <div>
                <h3 class="font-semibold text-gray-900">
                    Assets
                </h3>

                <p class="text-sm text-gray-500">
                    Manage assets attached to this Lite Integration.
                </p>
            </div>

            <button
                type="button"
                class="rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700"
                @click="openAssetSelector"
            >
                Add Asset
            </button>
        </div>

        <div
            v-if="error"
            class="m-4 rounded-md bg-red-50 p-3 text-sm text-red-700"
        >
            {{ error }}
        </div>

        <div
            v-if="loading"
            class="p-6 text-center text-sm text-gray-500"
        >
            Loading assets...
        </div>

        <div
            v-else-if="!assets.length"
            class="p-6 text-center text-sm text-gray-500"
        >
            No assets are attached to this Lite Integration.
        </div>

        <div
            v-else
            class="divide-y divide-gray-200"
        >
            <div
                v-for="asset in assets"
                :key="asset.uuid"
                class="flex items-center justify-between gap-4 px-5 py-4"
            >
                <div class="flex min-w-0 items-center gap-3">
                    <img
                        v-if="asset.image_url"
                        :src="asset.image_url"
                        :alt="asset.name"
                        class="h-12 w-12 shrink-0 rounded object-cover"
                    >

                    <div
                        v-else
                        class="flex h-12 w-12 shrink-0 items-center justify-center rounded bg-gray-100 text-xs text-gray-400"
                    >
                        N/A
                    </div>

                    <div class="min-w-0">
                        <div class="truncate font-medium text-gray-900">
                            {{ asset.name }}
                        </div>

                        <div class="truncate text-xs text-gray-400">
                            {{ asset.uuid }}
                        </div>
                    </div>
                </div>

                <div class="flex shrink-0 items-center gap-2">
                    <button
                        type="button"
                        class="rounded-md border border-blue-300 px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 disabled:opacity-50"
                        :disabled="
                            applyingAssetUuid === asset.uuid
                            || removingAssetUuid === asset.uuid
                        "
                        @click="applyToAllExperiences(asset)"
                    >
                        {{
                            applyingAssetUuid === asset.uuid
                                ? 'Applying...'
                                : 'Apply to all experiences'
                        }}
                    </button>

                    <button
                        type="button"
                        class="rounded-md border border-red-300 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 disabled:opacity-50"
                        :disabled="
                            removingAssetUuid === asset.uuid
                            || applyingAssetUuid === asset.uuid
                        "
                        @click="removeAsset(asset)"
                    >
                        {{
                            removingAssetUuid === asset.uuid
                                ? 'Removing...'
                                : 'Remove'
                        }}
                    </button>
                </div>
            </div>
        </div>

        <div
            v-if="selectorVisible"
            class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
            @click.self="closeAssetSelector"
        >
            <div class="w-full max-w-2xl rounded-lg bg-white shadow-xl">
                <div class="flex items-center justify-between border-b px-6 py-4">
                    <div>
                        <h3 class="text-lg font-semibold">
                            Add Asset
                        </h3>

                        <p class="text-sm text-gray-500">
                            Select an asset to attach to
                            {{ liteIntegration.name }}.
                        </p>
                    </div>

                    <button
                        type="button"
                        class="text-2xl leading-none text-gray-400 hover:text-gray-600"
                        @click="closeAssetSelector"
                    >
                        &times;
                    </button>
                </div>

                <div
                    v-if="selectorError"
                    class="m-4 rounded-md bg-red-50 p-3 text-sm text-red-700"
                >
                    {{ selectorError }}
                </div>

                <div
                    v-if="selectorLoading"
                    class="p-8 text-center text-sm text-gray-500"
                >
                    Loading available assets...
                </div>

                <div
                    v-else-if="!availableAssets.length"
                    class="p-8 text-center text-sm text-gray-500"
                >
                    No available assets found.
                </div>

                <div
                    v-else
                    class="max-h-[500px] divide-y divide-gray-200 overflow-y-auto"
                >
                    <div
                        v-for="asset in availableAssets"
                        :key="asset.uuid"
                        class="flex items-center justify-between gap-4 px-6 py-4"
                    >
                        <div class="flex min-w-0 items-center gap-3">
                            <img
                                v-if="asset.image_url"
                                :src="asset.image_url"
                                :alt="asset.name"
                                class="h-10 w-10 shrink-0 rounded object-cover"
                            >

                            <div
                                v-else
                                class="flex h-10 w-10 shrink-0 items-center justify-center rounded bg-gray-100 text-xs text-gray-400"
                            >
                                N/A
                            </div>

                            <div class="min-w-0">
                                <div class="truncate text-sm font-medium text-gray-900">
                                    {{ asset.name }}
                                </div>

                                <div class="truncate text-xs text-gray-400">
                                    {{ asset.uuid }}
                                </div>
                            </div>
                        </div>

                        <button
                            type="button"
                            class="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                            :disabled="addingAssetUuid === asset.uuid"
                            @click="addAsset(asset)"
                        >
                            {{
                                addingAssetUuid === asset.uuid
                                    ? 'Adding...'
                                    : 'Add'
                            }}
                        </button>
                    </div>
                </div>

                <div
                    v-if="selectorPagination.lastPage > 1"
                    class="flex items-center justify-between border-t px-6 py-4"
                >
                    <button
                        type="button"
                        class="rounded-md border px-3 py-1.5 text-sm disabled:opacity-50"
                        :disabled="selectorPagination.currentPage <= 1"
                        @click="loadAvailableAssets(
                            selectorPagination.currentPage - 1
                        )"
                    >
                        Previous
                    </button>

                    <span class="text-sm text-gray-500">
                        Page
                        {{ selectorPagination.currentPage }}
                        of
                        {{ selectorPagination.lastPage }}
                    </span>

                    <button
                        type="button"
                        class="rounded-md border px-3 py-1.5 text-sm disabled:opacity-50"
                        :disabled="
                            selectorPagination.currentPage >=
                            selectorPagination.lastPage
                        "
                        @click="loadAvailableAssets(
                            selectorPagination.currentPage + 1
                        )"
                    >
                        Next
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'LiteIntegrationAssets',

    props: {
        liteIntegration: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            assets: [],
            loading: false,
            error: null,

            selectorVisible: false,
            selectorLoading: false,
            selectorError: null,

            availableAssets: [],

            addingAssetUuid: null,
            removingAssetUuid: null,
            applyingAssetUuid: null,

            selectorPagination: {
                currentPage: 1,
                lastPage: 1,
            },
        };
    },

    mounted() {
        this.loadAssets();
    },

    methods: {
        async loadAssets() {
            this.loading = true;
            this.error = null;

            try {
                const response = await this.$http.get(
                    `/lite-integrations/${this.liteIntegration.uuid}/assets`
                );

                this.assets = response.data.data ?? [];
            } catch (error) {
                this.error = this.getErrorMessage(
                    error,
                    'Unable to load Lite Integration assets.'
                );
            } finally {
                this.loading = false;
            }
        },

        async openAssetSelector() {
            this.selectorVisible = true;

            await this.loadAvailableAssets();
        },

        closeAssetSelector() {
            if (this.addingAssetUuid) {
                return;
            }

            this.selectorVisible = false;
            this.selectorError = null;
            this.availableAssets = [];
        },

        async loadAvailableAssets(page = 1) {
            this.selectorLoading = true;
            this.selectorError = null;

            try {
                const response = await this.$http.get(
                    '/manage-assets',
                    {
                        params: {
                            page,
                        },
                    }
                );

                const assignedAssetUuids = new Set(
                    this.assets.map(asset => asset.uuid)
                );

                this.availableAssets = (
                    response.data.data ?? []
                ).filter(
                    asset => !assignedAssetUuids.has(asset.uuid)
                );

                this.selectorPagination = {
                    currentPage:
                        response.data.meta?.current_page ?? 1,

                    lastPage:
                        response.data.meta?.last_page ?? 1,
                };
            } catch (error) {
                this.selectorError = this.getErrorMessage(
                    error,
                    'Unable to load available assets.'
                );
            } finally {
                this.selectorLoading = false;
            }
        },

        async addAsset(asset) {
            if (this.addingAssetUuid) {
                return;
            }

            this.addingAssetUuid = asset.uuid;
            this.selectorError = null;

            try {
                await this.$http.post(
                    `/lite-integrations/${this.liteIntegration.uuid}/assets`,
                    {
                        asset_uuid: asset.uuid,
                    }
                );

                await this.loadAssets();

                this.availableAssets = this.availableAssets.filter(
                    availableAsset =>
                        availableAsset.uuid !== asset.uuid
                );

                this.$emit('assets-changed');
            } catch (error) {
                this.selectorError = this.getErrorMessage(
                    error,
                    'Unable to add the asset.'
                );
            } finally {
                this.addingAssetUuid = null;
            }
        },

        async removeAsset(asset) {
            if (
                this.removingAssetUuid
                || this.applyingAssetUuid
            ) {
                return;
            }

            const confirmed = window.confirm(
                `Remove "${asset.name}" from this Lite Integration?`
            );

            if (!confirmed) {
                return;
            }

            this.removingAssetUuid = asset.uuid;
            this.error = null;

            try {
                await this.$http.delete(
                    `/lite-integrations/${this.liteIntegration.uuid}`
                    + `/assets/${asset.uuid}`
                );

                this.assets = this.assets.filter(
                    existingAsset =>
                        existingAsset.uuid !== asset.uuid
                );

                this.$emit('assets-changed');
                this.$emit('experience-assets-changed');
            } catch (error) {
                this.error = this.getErrorMessage(
                    error,
                    'Unable to remove the asset.'
                );
            } finally {
                this.removingAssetUuid = null;
            }
        },

        async applyToAllExperiences(asset) {
            if (
                this.applyingAssetUuid
                || this.removingAssetUuid
            ) {
                return;
            }

            const confirmed = window.confirm(
                `Apply "${asset.name}" to all experiences in this Lite Integration?`
            );

            if (!confirmed) {
                return;
            }

            this.applyingAssetUuid = asset.uuid;
            this.error = null;

            try {
                const response = await this.$http.post(
                    `/lite-integrations/${this.liteIntegration.uuid}`
                    + `/assets/${asset.uuid}/apply-to-all-experiences`
                );

                this.$emit('experience-assets-changed');

                window.alert(response.data.message);
            } catch (error) {
                this.error = this.getErrorMessage(
                    error,
                    'Unable to apply the asset to all experiences.'
                );
            } finally {
                this.applyingAssetUuid = null;
            }
        },

        getErrorMessage(error, fallback) {
            return error.response?.data?.message ?? fallback;
        },
    },
};
</script>